import { useState } from "react";
import { Info, CheckCircle2, HelpCircle } from "lucide-react";

// PATCH 29 Small Wins — shared, dependency-light UI kit: glossary tooltips (SW6),
// "why we ask this" info tips (SW11/SW15), "last verified" badge (SW8) and a static
// illustrative BeCo Index visual (SW4). Greyscale shell only; no colour-only status.

export const GLOSSARY = {
  "BeCo Index": "A guide comparing your prompt's clarity to your app's complexity. Patent pending (Kenya, filed 13 September 2026). Not a validated psychological measure, and not a payment or fraud tool.",
  "Perimeter Gate": "A set of plain-English questions that flags regulated activities (money, credit, health, children's data) before you build, so nothing regulated slips through unnoticed.",
  "CRX": "Compliance Risk Index — an indicative read of how much regulatory attention your project needs. Not a pass mark and not legal advice.",
  "Evidence pack": "A dated record of which UK/EU compliance basics your build supports, how each was verified, and what still needs a human decision. Readiness evidence, not certification.",
  "Compliance layer": "One area of law or good practice (for example data protection or accessibility) that your app is checked against.",
  "Access class": "How you obtain access to a connector: A open/free, B commercial, C approval-gated. You always obtain your own access — Kadana never holds keys.",
  "Compliance-ready": "Built to support the relevant UK/EU basics, with evidence to show it. Never a guarantee of legal compliance — that stays with the app owner.",
};

// Small accessible tooltip: reveals on hover AND keyboard focus, dismisses on Escape/blur.
// PATCH 37 Mi2 — one standard reveal delay (150ms) and one placement for every tooltip.
const TOOLTIP_DELAY = 150;
export function InfoTip({ label, text, testid, icon: Icon = Info }) {
  const [open, setOpen] = useState(false);
  const [t, setT] = useState(null);
  const openSoon = () => { const id = setTimeout(() => setOpen(true), TOOLTIP_DELAY); setT(id); };
  const closeNow = () => { if (t) clearTimeout(t); setOpen(false); };
  return (
    <span style={{ position: "relative", display: "inline-flex", verticalAlign: "middle" }}>
      <button type="button" data-testid={testid} aria-label={label || "More information"}
        aria-expanded={open}
        onMouseEnter={openSoon} onMouseLeave={closeNow}
        onFocus={() => setOpen(true)} onBlur={closeNow}
        onKeyDown={(e) => { if (e.key === "Escape") closeNow(); }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        style={{ background: "none", border: "none", padding: 0, marginLeft: 5, cursor: "help",
          color: "var(--muted)", display: "inline-flex", lineHeight: 0 }}>
        <Icon size={13} />
      </button>
      {open && (
        <span role="tooltip" style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, zIndex: 60,
          width: 264, background: "var(--control)", border: "1px solid var(--border-strong)", borderRadius: 8,
          padding: "9px 11px", fontSize: 12, color: "var(--heading)", lineHeight: 1.5, fontWeight: 400,
          textAlign: "left", boxShadow: "0 6px 20px rgba(17,17,19,.45)" }}>{text}</span>
      )}
    </span>
  );
}

// Me14 — a "?" contextual-help affordance for any non-obvious admin/dashboard widget.
// Extends SW11's Perimeter-Gate pattern to the whole product with one consistent control.
export function HelpTip({ text, label, testid }) {
  return <InfoTip icon={HelpCircle} text={text} label={label || "What's this?"} testid={testid} />;
}

// Underlined jargon term with an attached glossary tooltip.
export function Glossary({ term, children }) {
  const text = GLOSSARY[term];
  if (!text) return <>{children || term}</>;
  const slug = term.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <span style={{ borderBottom: "1px dotted var(--border-strong)" }}>{children || term}</span>
      <InfoTip label={`What is ${term}?`} text={text} testid={`glossary-${slug}`} />
    </span>
  );
}

// SW8 — "Last verified [date]" badge (icon + text, never colour alone).
export function LastVerified({ date, prefix = "Last verified", testid = "last-verified" }) {
  const d = date ? String(date).slice(0, 10) : null;
  return (
    <span className="k-badge" data-testid={testid} style={{ fontSize: 11 }}
      title={d ? `${prefix} on ${d}` : "Not yet verified by an automated check"}>
      <CheckCircle2 size={11} /> {d ? `${prefix} ${d}` : "Not yet verified"}
    </span>
  );
}

// SW4 — illustrative BeCo Index visual (greyscale). Updates gently as sector/prompt are picked
// (no countdown, no urgency animation); shows a fixed illustrative sample before any selection.
export function BecoStaticVisual({ clarity = null, complexity = null }) {
  const live = clarity != null || complexity != null;
  const c = Math.max(0, Math.min(100, clarity ?? 0));
  const x = Math.max(0, Math.min(100, complexity ?? 0));
  const marker = live ? Math.max(6, Math.min(96, Math.round(50 + (c - x) / 2))) : 72;
  const state = !live ? "illustrative" : marker >= 60 ? "well-scoped" : marker >= 35 ? "getting there" : "add detail";
  return (
    <div className="k-container" data-testid="beco-static-visual" style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span className="k-heading" style={{ fontSize: 14, fontWeight: 700, display: "inline-flex", alignItems: "center" }}>
          <Glossary term="BeCo Index" />
        </span>
        <span className="k-badge k-badge-sandbox" data-testid="beco-state" style={{ fontSize: 10 }}>{state}</span>
      </div>
      <div style={{ position: "relative", height: 10, background: "var(--control)", border: "1px solid var(--border)",
        borderRadius: 999, marginTop: 12, overflow: "hidden" }} aria-hidden="true">
        <div data-testid="beco-marker" style={{ width: `${marker}%`, height: "100%", background: "var(--accent-primary)",
          transition: "width .4s cubic-bezier(0,0,.2,1)" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        <span className="k-muted" style={{ fontSize: 10 }}>vague prompt</span>
        <span className="k-muted" style={{ fontSize: 10 }}>clarity vs. complexity</span>
        <span className="k-muted" style={{ fontSize: 10 }}>well-scoped</span>
      </div>
      <p className="k-muted" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.6 }}>
        The BeCo Index compares how clearly you describe your app to how complex it is — a guide to help
        you tighten the brief{live ? ", moving gently as you pick a sector and add detail" : ""}. Illustrative
        only, not your project's score, and not a validated psychological measure. Patent pending (Kenya,
        filed 13 September 2026).
      </p>
    </div>
  );
}
